import { HiVolumeOff } from "react-icons/hi";
import { Pin, PinOff } from "lucide-react";
import ChatAvatar from "./ChatAvatar";
import { useChat } from "./ChatProvider";
import { formatListTime, messagePreview, otherMemberId } from "./chatUtils";

function ConversationListItem({ conversation, active, onSelect, onTogglePin }) {
  const { users, me, typing, titleOf } = useChat();
  const isGroup = conversation.type === "group";
  const other = isGroup ? null : users[otherMemberId(conversation, me?.id)];
  const typers = Object.keys(typing[conversation.id] || {});
  const unread = conversation.unreadCount > 0;
  const pinned = Boolean(conversation.pinnedAt);

  return (
    <div className="group relative" data-chat-list-item={conversation.id}>
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
          active ? "bg-blue-50 dark:bg-gray-700" : "hover:bg-gray-100 dark:hover:bg-gray-800"
        }`}
      >
        <ChatAvatar
          name={titleOf(conversation)}
          src={isGroup ? conversation.avatarThumbUrl : other?.avatarThumbUrl}
          seed={isGroup ? conversation.id : other?.id}
          group={isGroup}
          online={isGroup ? undefined : !!other?.online}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-sm ${unread ? "font-semibold text-gray-900 dark:text-white" : "font-medium text-gray-800 dark:text-gray-100"}`}>
              {titleOf(conversation)}
            </span>
            <span className={`shrink-0 text-[11px] ${unread ? "text-blue-600 font-semibold" : "text-gray-400"}`}>
              {formatListTime(conversation.lastMessageAt || conversation.createdAt)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-xs ${typers.length ? "text-blue-600 italic" : unread ? "text-gray-800 dark:text-gray-200" : "text-gray-500 dark:text-gray-400"}`}>
              {typers.length ? "typing…" : messagePreview(conversation.lastMessage, users, me?.id)}
            </span>
            <span className="flex items-center gap-1 shrink-0">
              {pinned && <Pin className="w-3.5 h-3.5 text-gray-400 rotate-45" aria-label="Pinned" />}
              {conversation.muted && <HiVolumeOff className="w-3.5 h-3.5 text-gray-400" title="Muted" />}
              {conversation.mentionCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center" title="You were mentioned">
                  @
                </span>
              )}
              {unread && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center">
                  {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
                </span>
              )}
            </span>
          </div>
        </div>
      </button>
      {onTogglePin && (
        // Shown over the time on hover/focus; a sibling of the row button (buttons can't be nested).
        <button
          type="button"
          onClick={() => onTogglePin(conversation)}
          className="absolute right-2 top-1.5 p-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white shadow-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
          title={pinned ? "Unpin chat" : "Pin chat to top"}
          aria-label={pinned ? `Unpin ${titleOf(conversation)}` : `Pin ${titleOf(conversation)} to top`}
        >
          {pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

export default ConversationListItem;
